import React from 'react';
import { Alert, Card, CardHeader, EmptyState, Skeleton } from '@opendate-app/setlist';
import { changePercent, compactCurrency } from '../../utils/reportingUtils';
import { REVENUE_STREAMS } from './revenueStreams';
import BarRow from './BarRow';

const STREAM_META = Object.fromEntries(REVENUE_STREAMS.map((s) => [s.key, s]));

const CHANGE_STYLE = { width: 56, textAlign: 'right' };

export function mixRows(streams = []) {
  const rows = streams
    .filter((s) => STREAM_META[s.key] && (s.current || s.prior))
    .sort((a, b) => b.current - a.current);
  const max = rows[0]?.current || 0;

  return rows.map((s) => ({
    ...STREAM_META[s.key],
    current: s.current,
    prior: s.prior,
    width: max > 0 ? Math.max(0, (s.current / max) * 100) : 0,
  }));
}

function Change({ current, prior }) {
  if (!prior) {
    if (current > 0) return <span style={{ ...CHANGE_STYLE, color: 'var(--sl-text-tertiary)' }}>New</span>;
    return <span style={CHANGE_STYLE} />;
  }

  const pct = changePercent(current, prior);
  const up = parseFloat(pct) >= 0;
  return (
    <span style={{ ...CHANGE_STYLE, color: up ? 'var(--sl-text-success)' : 'var(--sl-text-error)' }}>
      {up ? '+' : '−'}{pct.replace('-', '')}%
    </span>
  );
}

function StreamRow({ row }) {
  return (
    <BarRow
      label={row.label}
      value={compactCurrency(row.current)}
      trailing={<Change current={row.current} prior={row.prior} />}
      width={row.width}
      color={row.color}
      dotColor={row.color}
    />
  );
}

function RevenueMixCard({ data, loading, error, href, comparisonLabel }) {
  const rows = mixRows(data?.streams);
  const label = comparisonLabel || 'vs the previous period';

  return (
    <Card
      header={(
        <CardHeader
          title={<a href={href} style={{ color: 'inherit' }}>Where it came from →</a>}
          supportingText="Net to organizer"
        />
      )}
    >
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {Array.from({ length: REVENUE_STREAMS.length }, (_, i) => <Skeleton key={i} width="100%" height={28} />)}
        </div>
      )}
      {!loading && error && <Alert color="error" title="Couldn't load revenue" description="Refresh the page to try again." />}
      {!loading && !error && rows.length === 0 && <EmptyState size="sm" title="No revenue in this range" />}
      {!loading && !error && rows.length > 0 && (
        <div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {rows.map((row) => <StreamRow key={row.key} row={row} />)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--sl-text-tertiary)', marginTop: 16 }}>
            % change {label}. New means nothing came in during that period.
          </div>
        </div>
      )}
    </Card>
  );
}

export default RevenueMixCard;
